'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

const LIFTS = ['BB Bench', 'Barbell Row', 'Cable Row', 'Lat Pulldown', 'EZ Curl', 'DB Shoulder Press'];

const MILESTONE_TARGETS = {
  'BB Bench': [60, 70, 80, 90, 97.5],
  'Barbell Row': [60, 70, 80, 90, 100],
  'Cable Row': [50, 60, 70, 80, 90],
  'Lat Pulldown': [50, 60, 70, 80, 90],
  'EZ Curl': [25, 30, 35, 40, 45],
  'DB Shoulder Press': [16, 18, 20, 22, 24],
};

const SEED_DATA = {
  'BB Bench': [
    { date: '2026-03-12', weight: 45, reps: 8 },
    { date: '2026-03-19', weight: 48, reps: 7 },
    { date: '2026-03-26', weight: 50, reps: 6 },
    { date: '2026-04-02', weight: 52, reps: 5 },
  ],
  'Barbell Row': [
    { date: '2026-03-12', weight: 50, reps: 8 },
    { date: '2026-03-19', weight: 52, reps: 8 },
    { date: '2026-03-26', weight: 54, reps: 8 },
    { date: '2026-04-02', weight: 55, reps: 8 },
  ],
  'Cable Row': [
    { date: '2026-03-12', weight: 40, reps: 10 },
    { date: '2026-03-19', weight: 42, reps: 10 },
    { date: '2026-03-26', weight: 44, reps: 10 },
    { date: '2026-04-02', weight: 46, reps: 10 },
  ],
  'Lat Pulldown': [
    { date: '2026-03-12', weight: 40, reps: 10 },
    { date: '2026-03-19', weight: 42, reps: 10 },
    { date: '2026-03-26', weight: 43, reps: 10 },
    { date: '2026-04-02', weight: 45, reps: 10 },
  ],
  'EZ Curl': [
    { date: '2026-03-12', weight: 20, reps: 8 },
    { date: '2026-03-19', weight: 20.5, reps: 8 },
    { date: '2026-03-26', weight: 21, reps: 8 },
    { date: '2026-04-02', weight: 22.5, reps: 8 },
  ],
  'DB Shoulder Press': [
    { date: '2026-03-12', weight: 12, reps: 10 },
    { date: '2026-03-19', weight: 12.5, reps: 10 },
    { date: '2026-03-26', weight: 13, reps: 10 },
    { date: '2026-04-02', weight: 14, reps: 10 },
  ],
};

// Brzycki formula: weight × (36 / (37 - reps))
const calculateEstimated1RM = (weight, reps) => {
  if (reps < 1 || reps > 36) return weight;
  return weight * (36 / (37 - reps));
};

export default function OneRMTracker() {
  const [selectedLift, setSelectedLift] = useState('BB Bench');
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [data, setData] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Load data from localStorage on mount
  useEffect(() => {
    const storedData = localStorage.getItem('oneRMData');
    if (storedData) {
      try {
        setData(JSON.parse(storedData));
      } catch {
        setData(SEED_DATA);
      }
    } else {
      setData(SEED_DATA);
    }
    setIsLoaded(true);
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('oneRMData', JSON.stringify(data));
    }
  }, [data, isLoaded]);

  // Set weight and reps from last entry of selected lift
  useEffect(() => {
    if (isLoaded && data[selectedLift]?.length > 0) {
      const lastEntry = data[selectedLift][data[selectedLift].length - 1];
      setWeight(lastEntry.weight.toString());
      setReps(lastEntry.reps.toString());
    }
  }, [selectedLift, isLoaded, data]);

  const handleLogSet = () => {
    if (!weight || !reps || isNaN(weight) || isNaN(reps)) return;

    const today = new Date().toISOString().split('T')[0];
    const newSet = {
      date: today,
      weight: parseFloat(weight),
      reps: parseInt(reps, 10),
    };

    setData((prev) => ({
      ...prev,
      [selectedLift]: [...(prev[selectedLift] || []), newSet],
    }));
  };

  const handleDeleteSet = (liftName, index) => {
    setData((prev) => ({
      ...prev,
      [liftName]: prev[liftName].filter((_, i) => i !== index),
    }));
  };

  const handleClearAllData = () => {
    if (window.confirm('Are you sure you want to clear all 1RM data? This cannot be undone.')) {
      setData(SEED_DATA);
      setWeight('');
      setReps('');
    }
  };

  // Get current estimated 1RM for selected lift
  const currentEst1RM = useMemo(() => {
    if (!data[selectedLift]?.length) return 0;
    const lastEntry = data[selectedLift][data[selectedLift].length - 1];
    return calculateEstimated1RM(lastEntry.weight, lastEntry.reps);
  }, [selectedLift, data]);

  // Get chart data for selected lift
  const chartData = useMemo(() => {
    const liftData = data[selectedLift] || [];
    const labels = liftData.map((entry) => entry.date);
    const est1RMs = liftData.map((entry) => calculateEstimated1RM(entry.weight, entry.reps));

    return {
      labels,
      datasets: [
        {
          label: `${selectedLift} - Est 1RM (kg)`,
          data: est1RMs,
          borderColor: '#1a1a1a',
          backgroundColor: 'rgba(26, 26, 26, 0.05)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 5,
          pointBackgroundColor: '#1a1a1a',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        },
      ],
    };
  }, [selectedLift, data]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#1a1a1a',
          font: { size: 12, weight: '500' },
          padding: 12,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { color: '#666' },
      },
      x: {
        grid: { display: false },
        ticks: { color: '#666' },
      },
    },
  };

  // Calculate progress toward next milestone
  const getMilestoneProgress = (liftName) => {
    const est1RM = data[liftName]?.length
      ? calculateEstimated1RM(data[liftName][data[liftName].length - 1].weight, data[liftName][data[liftName].length - 1].reps)
      : 0;
    const targets = MILESTONE_TARGETS[liftName] || [];
    const nextTarget = targets.find((target) => target > est1RM) || targets[targets.length - 1];
    const prevTarget = targets.filter((target) => target < est1RM).pop() || 0;

    const progress = nextTarget ? ((est1RM - prevTarget) / (nextTarget - prevTarget)) * 100 : 100;
    return {
      est1RM: est1RM.toFixed(1),
      nextTarget: nextTarget || est1RM,
      progress: Math.min(100, Math.max(0, progress)),
    };
  };

  if (!isLoaded) return null;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>1RM Tracker</h1>
        <p style={{ fontSize: 14, color: '#888' }}>
          {currentEst1RM > 0
            ? `${selectedLift} — Current Est 1RM: ${currentEst1RM.toFixed(1)} kg`
            : 'Track your estimated one-rep max for key lifts'}
        </p>
      </div>

      {/* Lift Selector */}
      <div style={{ marginBottom: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
        {LIFTS.map((lift) => (
          <button
            key={lift}
            onClick={() => setSelectedLift(lift)}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: selectedLift === lift ? '2px solid #1a1a1a' : '1px solid #e0e0e0',
              backgroundColor: selectedLift === lift ? '#1a1a1a' : '#fff',
              color: selectedLift === lift ? '#fff' : '#1a1a1a',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {lift}
          </button>
        ))}
      </div>

      {/* Log Set Section */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '24px', marginBottom: '24px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1a1a1a' }}>Log a Set</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#666', marginBottom: '6px' }}>Weight (kg)</label>
            <input
              type="number"
              step="0.5"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="e.g. 50"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#666', marginBottom: '6px' }}>Reps</label>
            <input
              type="number"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              placeholder="e.g. 6"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        <button
          onClick={handleLogSet}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#1a1a1a',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = '#333')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = '#1a1a1a')}
        >
          Log Set
        </button>
      </div>

      {/* Chart Section */}
      {(data[selectedLift]?.length || 0) > 0 && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1a1a1a' }}>Progression Chart</h2>
          <div style={{ height: '300px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* History Section */}
      {(data[selectedLift]?.length || 0) > 0 && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1a1a1a' }}>Recent Sets</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ textAlign: 'left', padding: '12px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '12px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Weight (kg)</th>
                  <th style={{ textAlign: 'left', padding: '12px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Reps</th>
                  <th style={{ textAlign: 'left', padding: '12px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Est 1RM (kg)</th>
                  <th style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(data[selectedLift] || [])
                  .slice()
                  .reverse()
                  .map((set, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 0', fontSize: 13, color: '#1a1a1a' }}>{set.date}</td>
                      <td style={{ padding: '12px 0', fontSize: 13, color: '#1a1a1a' }}>{set.weight}</td>
                      <td style={{ padding: '12px 0', fontSize: 13, color: '#1a1a1a' }}>{set.reps}</td>
                      <td style={{ padding: '12px 0', fontSize: 13, color: '#1a1a1a', fontWeight: 600 }}>{calculateEstimated1RM(set.weight, set.reps).toFixed(1)}</td>
                      <td style={{ padding: '12px 0', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteSet(selectedLift, data[selectedLift].length - 1 - idx)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            color: '#d32f2f',
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#ffebee';
                            e.target.style.borderColor = '#d32f2f';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.borderColor = '#e0e0e0';
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Milestones Section */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '24px', marginBottom: '24px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: '#1a1a1a' }}>Milestones</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          {LIFTS.map((lift) => {
            const { est1RM, nextTarget, progress } = getMilestoneProgress(lift);
            return (
              <div key={lift}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{lift}</h3>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {est1RM} / {nextTarget.toFixed(1)} kg
                  </span>
                </div>
                <div style={{ height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${progress}%`,
                      backgroundColor: '#1a1a1a',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Clear All Data Button */}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button
          onClick={handleClearAllData}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: '1px solid #d32f2f',
            backgroundColor: '#fff',
            color: '#d32f2f',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#ffebee';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#fff';
          }}
        >
          Clear All Data
        </button>
      </div>
    </div>
  );
}
